/**
 * focus-carousel.js — Decléor
 *
 * Drives the "Focus actif phare" carousel: round ingredient thumbnails as the navigation, one slide
 * per active, and an autoplay whose countdown is the ring drawn around the current thumbnail.
 *
 * Self-contained, no dependencies, no-ops when its markup is absent, and safe to load on every page.
 *
 * FIVE DECISIONS, ALL OF THEM LOAD-BEARING
 * ---------------------------------------------------------------------------
 * 1. THE TRACK IS A REAL SCROLL CONTAINER, not a transformed strip.
 *    assets/smooth-scroll.js boots Lenis on the window with allowNestedScroll:true, so any ancestor
 *    that can still scroll in the gesture direction wins the wheel event — a native overflow-x
 *    scroller is therefore protected from "smooth scroll ate my slider" for free, and a trackpad
 *    swipe (which emits deltaX AND deltaY) behaves. syncTouch is false, so touch is 100% native:
 *    CSS scroll-snap and swipe momentum come from the browser, not from us.
 *
 * 2. MOVING `.is-active` IS THE ONLY THING THAT STARTS OR RESTARTS THE COUNTDOWN.
 *    The keyframes are scoped to `.dc-fa__dot.is-active .dc-fa__ring circle`, so the cascade hands
 *    the newly active dot a fresh animation beginning at zero and takes the old dot's away. There is
 *    no inline animation style, no forced reflow, no restart helper.
 *
 *    The first version of this file did have one, and it was wrong twice over. It cleared the
 *    animation and re-added it around a read of `el.offsetWidth` — but offsetWidth does not exist on
 *    an SVG element, so the read returned undefined and forced no layout, leaving the restart at the
 *    mercy of style-change coalescing. And because the resting state was then a FULL ring, the gap
 *    between the class moving and the animation taking hold painted the whole circle: a flash on
 *    every click and every advance. Both problems disappear with the cascade doing the work and an
 *    empty resting state; see the CSS comment in sections/section-product-focus.liquid.
 *
 * 3. THE SCROLL LISTENER ONLY SPEAKS ONCE THE SCROLLING HAS STOPPED.
 *    It used to reconcile the active dot on every scroll event. During the ~400ms of a programmatic
 *    smooth scroll it kept finding the OLD slide nearest, moved `.is-active` back onto it, and
 *    restarted the countdown — two or three times per transition, which is what made the ring look
 *    erratic. Now a 120ms quiet period ends the gesture, and only then is the index reconciled. That
 *    covers a finger swipe and a programmatic scroll with the same rule, so the two cannot fight.
 *
 * 4. AUTOPLAY IS DISARMED UNDER prefers-reduced-motion, and that is not politeness.
 *    snippets/luxe-overrides.liquid forces every animation-duration to 0.001ms globally with
 *    !important. The countdown would finish instantly and fire animationend as fast as the event
 *    queue allows. The `dc-fa--no-count` class this file then sets kills the animation outright and
 *    leaves a plain complete ring as the selection mark; the MIN_ELAPSED check is a second net.
 *
 * 5. GEOMETRY IS MEASURED, NEVER ASSUMED.
 *    getBoundingClientRect deltas plus the track's own scrollLeft — correct in LTR and in RTL (the
 *    theme supports RTL), with no knowledge of slide widths or gaps. Element.scrollTo rather than
 *    scrollIntoView, so the WINDOW is never scrolled: smooth-scroll.js routes window.scrollTo through
 *    Lenis, and a nav click must not move the page.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-fa-carousel]';
  var SETTLE_MS = 120; // quiet period that marks the end of a scroll gesture
  var MIN_ELAPSED = 300; // an animationend sooner than this is not a real countdown

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  // The guard lives in a WeakMap, not in a DOM attribute: the theme editor morphs markup, and a
  // morph can strip an attribute while keeping the node — which would double-bind every listener.
  // The value is a re-sync callback, because a node that SURVIVES a morph keeps its listeners but
  // loses the classes and inline styles this file put on it. Rebinding would double the listeners;
  // doing nothing left it with no active dot, no ring and dead thumbnails.
  var bound = new WeakMap();

  function init(root) {
    if (!root) return;
    var resync = bound.get(root);
    if (resync) {
      resync();
      return;
    }

    var track = root.querySelector('[data-fa-track]');
    var slides = toArray(root.querySelectorAll('[data-fa-slide]'));
    var dots = toArray(root.querySelectorAll('[data-fa-dot]'));

    // One active is not a carousel, and the section renders the single layout in that case anyway.
    if (!track || slides.length < 2) return;

    var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var delay = parseInt(root.getAttribute('data-fa-delay'), 10);
    var autoplay = root.getAttribute('data-fa-autoplay') === 'true' && !reduced && delay > 0;

    if (!autoplay) root.classList.add('dc-fa--no-count');

    var current = 0;
    var onScreen = false;
    var startedAt = Date.now();
    var settleTimer = null;
    var programmatic = false;
    var pendingAdvance = false;
    var observer = null;

    // THREE reasons, not one boolean. A single flag meant whichever source released first cancelled
    // the other two — a finger lift un-pausing a hover, and worse. Each listener owns its own flag.
    var pHover = false;
    var pFocus = false;
    var pTouch = false;
    function paused() {
      return pHover || pFocus || pTouch;
    }

    function ringOf(i) {
      return dots[i] ? dots[i].querySelector('[data-fa-ring]') : null;
    }

    // The inline play-state is the ONE thing this file writes onto a ring. It has to be cleared from
    // every other dot, or a ring that was paused while active would come back frozen next time.
    function applyPlayState() {
      for (var i = 0; i < dots.length; i++) {
        var r = ringOf(i);
        if (!r) continue;
        if (i === current && autoplay && (paused() || !onScreen)) r.style.animationPlayState = 'paused';
        else r.style.animationPlayState = '';
      }
      // An animationend that arrived while paused or off screen was dropped, and `forwards` leaves
      // the ring full with nothing left to fire — the countdown would never advance again. Deferred
      // rather than lost, and released here as soon as it is legal to move.
      if (pendingAdvance && autoplay && !paused() && onScreen) {
        pendingAdvance = false;
        go(current + 1, true);
      }
    }

    function paintDots() {
      for (var i = 0; i < dots.length; i++) {
        var on = i === current;
        dots[i].classList.toggle('is-active', on);
        // aria-current, not aria-selected: these are navigation buttons, not a tab widget with
        // roving focus. The label names the active, so the current one is announced as such.
        if (on) dots[i].setAttribute('aria-current', 'true');
        else dots[i].removeAttribute('aria-current');
      }
      // A slide scrolled out of view still held its links in the tab order, so tabbing quietly
      // drove the carousel to a slide the shopper could not see. `inert` takes the whole subtree
      // out; aria-hidden covers engines that do not support it yet.
      for (var j = 0; j < slides.length; j++) {
        var isCur = j === current;
        slides[j].inert = !isCur;
        if (isCur) slides[j].removeAttribute('aria-hidden');
        else slides[j].setAttribute('aria-hidden', 'true');
      }
      startedAt = Date.now();
      applyPlayState();
    }

    // Hand the outgoing ring its unwind. A CSS transition fires on a change of the BASE value, and
    // removing an animation does not change the base — the ring would snap from wherever the
    // countdown had reached straight back to empty, at full opacity, in one frame. So: read the
    // animated value while it still applies, pin it inline (the base is now that value, and nothing
    // moves), then release it on the next frame so the transition runs from there to the resting 100.
    function unwind(i) {
      var r = ringOf(i);
      if (!r || reduced) return;
      var at = getComputedStyle(r).strokeDashoffset;
      if (!at || parseFloat(at) >= 99.5) return; // nothing drawn, nothing to unwind
      r.style.strokeDashoffset = at;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          r.style.strokeDashoffset = '';
        });
      });
    }

    function setIndex(i) {
      var next = ((i % slides.length) + slides.length) % slides.length;
      if (next === current) return false;
      var leaving = current;
      current = next;
      unwind(leaving); // must run BEFORE the class moves, while the animation still applies
      paintDots(); // this alone restarts the countdown — see decision 2
      return true;
    }

    function scrollToIndex(i, smooth) {
      var slide = slides[i];
      if (!slide) return;
      var left = slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      programmatic = Math.abs(track.scrollLeft - left) > 2;
      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: left, behavior: smooth && !reduced ? 'smooth' : 'auto' });
      } else {
        track.scrollLeft = left;
      }
    }

    function go(i, smooth) {
      setIndex(i);
      scrollToIndex(current, smooth !== false);
    }

    // Nearest leading edge — direction-agnostic, and indifferent to slide width, gap and how the
    // snap resolved.
    function indexFromScroll() {
      var base = track.getBoundingClientRect().left;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs(slides[i].getBoundingClientRect().left - base);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      return best;
    }

    /* ---- events ------------------------------------------------------------------------------ */

    for (var d = 0; d < dots.length; d++) {
      (function (i) {
        dots[i].addEventListener('click', function () {
          go(i, true);
        });
      })(d);
    }

    // Only OUR OWN scroll is deferred — see decision 3. A finger swipe reconciles live, because the
    // thumbnails sit directly above the slides and a nav that lags the whole gesture plus 120ms reads
    // as broken. setIndex() returns early when the index has not changed, so a live reconcile costs
    // one comparison per frame and repaints only on a real crossing.
    track.addEventListener(
      'scroll',
      function () {
        if (!programmatic) {
          setIndex(indexFromScroll());
          return;
        }
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(function () {
          settleTimer = null;
          programmatic = false;
          setIndex(indexFromScroll());
        }, SETTLE_MS);
      },
      { passive: true }
    );

    for (var r2 = 0; r2 < dots.length; r2++) {
      var ring = ringOf(r2);
      if (!ring) continue;
      ring.addEventListener('animationend', function (e) {
        if (!autoplay) return;
        if (e.target !== ringOf(current)) return; // a stale ring finishing its own animation
        if (Date.now() - startedAt < MIN_ELAPSED) return; // see decision 4
        if (paused() || !onScreen) {
          pendingAdvance = true; // released by applyPlayState when the pause lifts
          return;
        }
        go(current + 1, true);
      });
    }

    // Bound to the TRACK, not to the section. The pointer has to cross the section to reach a
    // thumbnail, so pausing on the section meant every click paused the countdown it had just
    // started — and with an empty resting state that left the chosen dot with no ring at all until
    // the pointer wandered off. The nav is for choosing; the track is what someone reads.
    track.addEventListener('mouseenter', function () {
      pHover = true;
      applyPlayState();
    });
    track.addEventListener('mouseleave', function () {
      pHover = false;
      applyPlayState();
    });
    track.addEventListener('focusin', function () {
      pFocus = true;
      applyPlayState();
    });
    track.addEventListener('focusout', function () {
      if (!track.contains(document.activeElement)) {
        pFocus = false;
        applyPlayState();
      }
    });
    // A touch means the shopper is driving. Autoplay does not fight a finger — but it does have to
    // start again afterwards: without these two releases one swipe suspended autoplay for the life
    // of the page, and no ring was ever drawn again.
    function touchRelease() {
      pTouch = false;
      applyPlayState();
    }
    track.addEventListener(
      'touchstart',
      function () {
        pTouch = true;
        applyPlayState();
      },
      { passive: true }
    );
    track.addEventListener('touchend', touchRelease, { passive: true });
    track.addEventListener('touchcancel', touchRelease, { passive: true });

    // Reduced motion read once was a trap of its own: turning it on mid-session left the countdown
    // running against luxe-overrides' 0.001ms override.
    if (window.matchMedia) {
      var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      var onMq = function () {
        reduced = mq.matches;
        autoplay = root.getAttribute('data-fa-autoplay') === 'true' && !reduced && delay > 0;
        root.classList.toggle('dc-fa--no-count', !autoplay);
        applyPlayState();
      };
      if (mq.addEventListener) mq.addEventListener('change', onMq);
      else if (mq.addListener) mq.addListener(onMq);
    }

    // Off-screen the countdown is paused rather than cleared: nothing runs, nothing is repainted,
    // and coming back resumes where it stopped instead of snapping the ring back to empty.
    if (typeof IntersectionObserver === 'function') {
      // threshold 0 with a negative rootMargin, not a ratio: a section taller than the viewport can
      // never reach a 0.25 ratio, so a ratio threshold stops delivering transitions entirely on a
      // tall slide and `onScreen` freezes at whatever the first observation happened to say.
      observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            onScreen = entries[i].isIntersecting;
          }
          applyPlayState();
        },
        { threshold: 0, rootMargin: '-10% 0px -10% 0px' }
      );
      observer.observe(root);
    } else {
      onScreen = true;
    }

    root.addEventListener('dc-fa:teardown', function () {
      if (observer) observer.disconnect();
      if (settleTimer) clearTimeout(settleTimer);
    });

    bound.set(root, function () {
      if (!autoplay) root.classList.add('dc-fa--no-count');
      paintDots();
    });

    paintDots();
    // No initial scroll: slide 0 is already in view, and scrolling to it would fight a deep link.
  }

  function boot(scope) {
    var nodes = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  // Re-init hooks. `theme:section:update` is dispatched on WINDOW (assets/theme.js:25441) — a
  // document listener never fires, which assets/custom.js:29-31 warns about in as many words and
  // which this file got wrong until it was reviewed: the handler had never run once. Its detail
  // carries a sectionId rather than a node, so there is nothing to scope to.
  window.addEventListener('theme:section:update', function () {
    boot(document);
  });
  // And the editor's own event, which is what actually fires when a section setting changes. The
  // <script src> inside the replaced markup does not re-execute, so without this the new nodes stay
  // unbound: no ring anywhere, no autoplay, dead thumbnails until a full reload.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e && e.target && e.target.nodeType === 1 ? e.target : document);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e && e.target && e.target.querySelector ? e.target.querySelector(SELECTOR) : null;
    if (el) el.dispatchEvent(new CustomEvent('dc-fa:teardown'));
  });
})();
